import { useEffect, useState } from "react";
import type { GuardianLinkRequestDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import {
  approveGuardianLinkRequest,
  fetchAdminGuardianLinkRequests,
  rejectGuardianLinkRequest
} from "../../shared/api/guardian";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/guardian-links", label: "紐付け一覧" }
];

export function AdminGuardianLinkRequestsPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [requests, setRequests] = useState<GuardianLinkRequestDto[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !session?.member?.roles.includes("admin")) {
      return;
    }

    let cancelled = false;

    async function loadRequests() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const response = await fetchAdminGuardianLinkRequests(tenantSlug);

        if (cancelled) {
          return;
        }

        setRequests(response.items);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "申請一覧の取得に失敗しました");
      }
    }

    void loadRequests();

    return () => {
      cancelled = true;
    };
  }, [session, status, tenantSlug]);

  async function reloadRequests() {
    const response = await fetchAdminGuardianLinkRequests(tenantSlug);
    setRequests(response.items);
  }

  async function handleReview(
    requestId: string,
    action: "approve" | "reject"
  ) {
    setBusyRequestId(requestId);
    setLoadError(null);

    try {
      if (action === "approve") {
        await approveGuardianLinkRequest(tenantSlug, requestId);
      } else {
        await rejectGuardianLinkRequest(tenantSlug, requestId);
      }

      await reloadRequests();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "レビュー処理に失敗しました");
    } finally {
      setBusyRequestId(null);
    }
  }

  return (
    <PageScaffold
      title="Admin Guardian Link Requests"
      description="保護者紐付け申請の最小レビュー画面です。approve は studentName / birthDate の in-memory match で link を作成し、reject は status を更新します。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            admin review には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock operator
            login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !session?.member?.roles.includes("admin") ? (
          <p>この画面は admin role のみ利用できます。</p>
        ) : null}
        {loadState === "loading" ? <p>申請一覧を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {status === "authenticated" && session?.member?.roles.includes("admin") ? (
          <div style={{ display: "grid", gap: 12 }}>
            {requests.length === 0 ? <p style={{ margin: 0 }}>未処理の申請はありません。</p> : null}

            {requests.map((request) => {
              const isBusy = busyRequestId === request.requestId;

              return (
                <article
                  key={request.requestId}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 8
                  }}
                >
                  <div>
                    <strong>{request.studentName}</strong> / {request.requestedRelationType}
                  </div>
                  <div>requestId: {request.requestId}</div>
                  <div>status: {request.status}</div>
                  <div>birthDate: {request.birthDate ?? "not provided"}</div>
                  <div>supportingInfo: {request.supportingInfo ?? "none"}</div>
                  <div>resolvedStudentIds: {request.resolvedStudentIds.join(", ") || "none"}</div>

                  {request.status === "submitted" ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => void handleReview(request.requestId, "approve")}
                        disabled={isBusy}
                      >
                        {isBusy ? "処理中..." : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleReview(request.requestId, "reject")}
                        disabled={isBusy}
                      >
                        {isBusy ? "処理中..." : "Reject"}
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}
      </div>
    </PageScaffold>
  );
}
